import { useCallback, useMemo, useState } from 'react'

interface UseLoadMoreResult<T> {
  visibleItems: T[]
  loadMore: () => void
  hasMore: boolean
  newItems: T[] // New items added in the latest load
}

const useLoadMore = <T,>(
  initialItems: T[],
  itemsPerPage: number = 10
): UseLoadMoreResult<T> => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [newItems, setNewItems] = useState<T[]>([])

  const visibleItems = useMemo(
    () => initialItems.slice(0, currentPage * itemsPerPage),
    [initialItems, currentPage, itemsPerPage]
  )

  const loadMore = useCallback(() => {
    setCurrentPage((prevPage) => {
      const nextPage = prevPage + 1
      const newVisibleItems = initialItems.slice(
        prevPage * itemsPerPage,
        nextPage * itemsPerPage
      )
      setNewItems(newVisibleItems) // Store the newly added items
      return nextPage
    })
  }, [initialItems, itemsPerPage])

  const hasMore = useMemo(
    () => visibleItems.length < initialItems.length,
    [visibleItems, initialItems]
  )

  return { visibleItems, loadMore, hasMore, newItems }
}

export default useLoadMore
